import {
  Block,
  Flex,
  Modal,
  ProductColor,
  Table,
  Tbody,
  Thead,
  Tr,
  Typography,
} from '@aloft/toolkit-sui';
// eslint-disable-next-line aloft/no-sui-chakra
import { ListItem, UnorderedList } from '@aloft/toolkit-sui/chakra';

import { useMemo } from 'react';

import { QualityTierBreakdownData } from '../../types.ts';
import { QualityTierChart } from './QualityTierChart.tsx';
import { getQualityTierChartData } from './getQualityTierChartData.ts';

export const QualityTierBreakdown = ({
  isOpen,
  onClose,
  qualityTierBreakdownData,
  selectedIds = [],
}: {
  isOpen: boolean;
  onClose: () => void;
  qualityTierBreakdownData?: QualityTierBreakdownData;
  selectedIds?: string[];
}) => {
  const chartData = useMemo(
    () =>
      qualityTierBreakdownData
        ? getQualityTierChartData(qualityTierBreakdownData, selectedIds)
        : undefined,
    [qualityTierBreakdownData, selectedIds],
  );

  if (!qualityTierBreakdownData) {
    return null;
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Quality Tier Analysis" size="4xl">
      <Flex flexDirection="column" gap="16px">
        <Block>
          <Typography variant="body1">
            In most housing markets, properties naturally group into tiers based on condition and
            quality.
          </Typography>
          <UnorderedList marginTop="0.5rem" lineHeight="1.5">
            <ListItem>
              <Typography variant="body1">
                <span style={{ fontWeight: 'bold' }}>Upper tiers (Tier 1)&nbsp;</span>
                generally include homes in C1–C2 condition, reflecting newer or highly renovated
                properties with premium finishes.
              </Typography>
            </ListItem>
            <ListItem>
              <Typography variant="body1">
                <span style={{ fontWeight: 'bold' }}>Middle tiers (Tier 2–3)&nbsp;</span>
                often represent C3 condition homes—well maintained but not recently upgraded.
              </Typography>
            </ListItem>
            <ListItem>
              <Typography variant="body1">
                <span style={{ fontWeight: 'bold' }}>Lower tiers (Tier 4 and below)&nbsp;</span>
                typically include C4–C6 condition homes showing deferred maintenance or dated
                features.
              </Typography>
            </ListItem>
          </UnorderedList>
          <Typography variant="body1" __dangerDoNotUseSx={{ marginTop: '0.5rem' }}>
            Our algorithm detects price clustering and “gap-ups” between these groups to identify
            natural market segments. These tiers help the appraiser quickly understand how quality
            and condition influence pricing within a defined market area.
          </Typography>
        </Block>
        <Block>
          <Table variant="grid">
            <Thead>
              <Tr>
                <th style={{ borderRight: 'none' }} />
                <th style={{ borderLeft: 'none', borderRight: 'none', textAlign: 'start' }}>
                  <Typography variant="h4">Quality Tiers</Typography>
                </th>
                <th style={{ borderLeft: 'none', borderRight: 'none', textAlign: 'start' }}>
                  <Typography variant="h4">Price Band</Typography>
                </th>
                <th style={{ borderLeft: 'none', borderRight: 'none', textAlign: 'start' }}>
                  <Typography variant="h4">Median GLA</Typography>
                </th>
                <th style={{ borderLeft: 'none', textAlign: 'start' }}>
                  <Typography variant="h4">Count of Sales</Typography>
                </th>
              </Tr>
            </Thead>
            <Tbody>
              {Object.entries(qualityTierBreakdownData).map(([qualityTier, data]) => (
                <Tr key={qualityTier}>
                  <td style={{ borderRight: 'none' }}>
                    <Block
                      backgroundColor={`qualityTier${qualityTier}` as ProductColor}
                      width="16px"
                      height="16px"
                      marginBottom="-2px"
                    />
                  </td>
                  <td style={{ borderLeft: 'none', borderRight: 'none' }}>
                    <Typography variant="body1">Tier {qualityTier}</Typography>
                  </td>
                  <td style={{ borderLeft: 'none', borderRight: 'none' }}>
                    <Typography variant="body1">{data.priceBand}</Typography>
                  </td>
                  <td style={{ borderLeft: 'none', borderRight: 'none' }}>
                    <Typography variant="body1">{data.medianGla}</Typography>
                  </td>
                  <td style={{ borderLeft: 'none' }}>
                    <Typography variant="body1">{data.countOfSales}</Typography>
                  </td>
                </Tr>
              ))}
            </Tbody>
          </Table>
        </Block>
        {chartData && chartData.points.length >= 2 ? (
          <QualityTierChart chartData={chartData} />
        ) : (
          <Block border="1px solid" borderColor="strokeDefault" padding="16px" width="100%">
            <Typography variant="body2" color="textSecondary">
              {chartData?.isLoadingConditionRatings
                ? 'Loading condition ratings…'
                : 'Sale price vs. condition will appear once enough properties have condition ratings.'}
            </Typography>
          </Block>
        )}
      </Flex>
    </Modal>
  );
};
